import React, { useEffect, useMemo, useState } from 'react'
import { App, ConfigProvider, theme, type ThemeConfig } from 'antd'

const readCssVar = (name: string, fallback: string) => {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

const resolveTheme = (): ThemeConfig => {
  const isDark =
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')

  const borderRadius = Number.parseFloat(readCssVar('--radius-md', '10')) || 10
  const borderRadiusLG = Number.parseFloat(readCssVar('--radius-card', '16')) || 16

  return {
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: readCssVar('--portal-solid', '#06b6d4'),
      colorInfo: readCssVar('--color-info', '#06b6d4'),
      colorSuccess: readCssVar('--color-success', '#10b981'),
      colorWarning: readCssVar('--color-warning', '#fcaa67'),
      colorError: readCssVar('--color-error', '#ff604a'),
      colorBgBase: readCssVar('--color-bg-primary', '#fafafa'),
      colorText: readCssVar('--color-text-primary', '#18181b'),
      colorBorder: readCssVar('--color-border-default', '#e4e4e7'),
      borderRadius,
      borderRadiusLG,
      fontFamily: readCssVar('--font-body', 'Inter, system-ui, sans-serif'),
      colorTextLightSolid: '#ffffff',
    },
    components: {
      Card: { borderRadiusLG },
    },
  }
}

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(() => resolveTheme())

  useEffect(() => {
    const updateTheme = () => setThemeConfig(resolveTheme())

    updateTheme()

    const root = document.documentElement
    const observer = new MutationObserver(updateTheme)
    observer.observe(root, { attributes: true, attributeFilter: ['class', 'style', 'data-portal'] })

    window.addEventListener('wasilah-theme-change', updateTheme)
    window.addEventListener('storage', updateTheme)

    return () => {
      observer.disconnect()
      window.removeEventListener('wasilah-theme-change', updateTheme)
      window.removeEventListener('storage', updateTheme)
    }
  }, [])

  const stableTheme = useMemo(() => themeConfig, [themeConfig])

  return (
    <ConfigProvider theme={stableTheme}>
      <App>{children}</App>
    </ConfigProvider>
  )
}